const STORAGE_KEY = 'songyingDisplayCurrency';
const RATES_STORAGE_KEY = 'songyingDisplayCurrencyRates';
const CACHE_DURATION = 12 * 60 * 60 * 1000;
const FALLBACK_RATES = Object.freeze({ USD: 1, HKD: 7.8, TWD: 30.6 });
const PRICE_CONTAINER_SELECTOR = [
  '.price',
  'price-component',
  '[class*="price__"]',
  '[class*="price-item"]',
  '[data-testid*="price"]',
  '.compare-at-price',
  '.jz-product > strong',
  '.jz-product-modal__price',
  '.cart__total',
  '.totals__total-value',
].join(',');
const USD_PRICE_PATTERN = /\$\s*([0-9][0-9,]*(?:\.\d{1,2})?)\s*(?:USD)?/gi;

class SongYingCurrencySelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.select = this.querySelector('[data-songying-currency-select]');
    this.note = this.querySelector('[data-songying-currency-note]');
    this.locale = this.dataset.locale === 'en' ? 'en-US' : 'zh-Hant';
    this.currency = this.readStoredCurrency();
    this.rates = { ...FALLBACK_RATES };
    this.textState = new WeakMap();
    this.isApplying = false;

    if (!this.select) return;
    this.select.value = this.currency;
    this.updateNote();
    this.select.addEventListener('change', () => this.changeCurrency(this.select.value));

    this.observer = new MutationObserver((mutations) => {
      if (this.isApplying) return;
      const roots = new Set();
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') roots.add(mutation.target.parentElement);
        mutation.addedNodes.forEach((node) => roots.add(node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement));
      });
      roots.forEach((root) => this.convertPrices(root));
    });
    this.observer.observe(document.body, { childList: true, characterData: true, subtree: true });

    this.convertPrices(document);
    this.loadRates().finally(() => this.convertPrices(document));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  readStoredCurrency() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && Object.hasOwn(FALLBACK_RATES, stored)) return stored;
    } catch (_error) {
      // Storage may be unavailable in strict privacy modes; USD remains the safe default.
    }
    return 'USD';
  }

  changeCurrency(currency) {
    if (!Object.hasOwn(FALLBACK_RATES, currency)) return;
    this.currency = currency;
    try {
      window.localStorage.setItem(STORAGE_KEY, currency);
    } catch (_error) {
      // The current page can still update even when storage is unavailable.
    }
    document.querySelectorAll('songying-currency-selector [data-songying-currency-select]').forEach((select) => {
      select.value = currency;
    });
    document.querySelectorAll('songying-currency-selector').forEach((selector) => {
      selector.currency = currency;
      selector.updateNote?.();
    });
    this.convertPrices(document);
    document.dispatchEvent(new CustomEvent('songying:display-currency-change', { detail: { currency } }));
  }

  updateNote() {
    if (this.note) this.note.hidden = this.currency === 'USD';
  }

  async loadRates() {
    const cached = this.readCachedRates();
    if (cached) this.rates = { ...this.rates, ...cached };

    try {
      const response = await fetch(this.dataset.rateUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Rate request failed with ${response.status}`);
      const payload = await response.json();
      const rates = Array.isArray(payload)
        ? Object.fromEntries(payload.map((item) => [item.quote, Number(item.rate)]))
        : payload.rates;
      if (!Number.isFinite(Number(rates?.HKD)) || !Number.isFinite(Number(rates?.TWD))) return;
      this.rates = { USD: 1, HKD: Number(rates.HKD), TWD: Number(rates.TWD) };
      try {
        window.localStorage.setItem(RATES_STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), rates: this.rates }));
      } catch (_error) {
        // A successful live rate can still be used for this page view.
      }
    } catch (_error) {
      // Cached or conservative fallback reference rates keep display conversion available.
    }
  }

  readCachedRates() {
    try {
      const cached = JSON.parse(window.localStorage.getItem(RATES_STORAGE_KEY));
      if (!cached?.timestamp || Date.now() - cached.timestamp > CACHE_DURATION) return null;
      if (!Number.isFinite(Number(cached.rates?.HKD)) || !Number.isFinite(Number(cached.rates?.TWD))) return null;
      return { USD: 1, HKD: Number(cached.rates.HKD), TWD: Number(cached.rates.TWD) };
    } catch (_error) {
      return null;
    }
  }

  convertPrices(root) {
    if (!root || this.isApplying) return;
    this.isApplying = true;
    const containers = new Set();

    if (root.nodeType === Node.ELEMENT_NODE && root.matches?.(PRICE_CONTAINER_SELECTOR)) containers.add(root);
    root.querySelectorAll?.(PRICE_CONTAINER_SELECTOR).forEach((element) => containers.add(element));

    containers.forEach((container) => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) this.convertTextNode(node);
    });
    this.isApplying = false;
  }

  convertTextNode(node) {
    if (!node.nodeValue || !/\$|USD/i.test(node.nodeValue)) return;
    const previous = this.textState.get(node);
    const source = previous && node.nodeValue === previous.converted ? previous.source : node.nodeValue;
    const converted = source.replace(USD_PRICE_PATTERN, (_match, amount) => {
      const usd = Number(amount.replaceAll(',', ''));
      if (!Number.isFinite(usd)) return _match;
      return this.formatAmount(usd);
    });
    this.textState.set(node, { source, converted });
    if (node.nodeValue !== converted) node.nodeValue = converted;
  }

  formatAmount(usd) {
    if (this.currency === 'USD') {
      return `$${usd.toLocaleString(this.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    }
    const converted = usd * (this.rates[this.currency] || FALLBACK_RATES[this.currency]);
    const symbol = this.currency === 'HKD' ? 'HK$' : 'NT$';
    return `≈ ${symbol}${converted.toLocaleString(this.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

if (!customElements.get('songying-currency-selector')) {
  customElements.define('songying-currency-selector', SongYingCurrencySelector);
}
